import {
  afterNextRender,
  Component,
  computed,
  DestroyRef,
  inject,
  OnInit,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RouterOutlet } from '@angular/router';
import { NgScrollbar } from 'ngx-scrollbar';
import { AuthManager } from '@infra-adapters/services/auth.service';
import { SessionExpiryRedirectService } from '@infra-adapters/services/session-expiry-redirect.service';
import { MedicalRecordBackofficePermissionsPolicy } from '@core-rules/medical-record/medical-record-backoffice-permissions.policy';
import { canManageUsersFromSession } from '@core-interfaces/user-admin/user-admin.permissions';
import { canManageRbacFromSession } from '@core-interfaces/rbac/rbac-management.permissions';
import { canViewDashboardFromSession } from '@core-interfaces/rbac/dashboard.permissions';
import { canManageAttachmentsAdminFromSession } from '@core-interfaces/rbac/attachments-admin.permissions';
import { canViewAiIncidentsFromSession } from '@core-interfaces/rbac/ai-incidents.permissions';
import { canReadBillingFromSession } from '@core-interfaces/rbac/membership-billing.permissions';
import { canManageTermsFromSession } from '@core-interfaces/rbac/terms-manage.permissions';
import { canManageBannersFromSession } from '@core-interfaces/rbac/banners-manage.permissions';
import { RBAC_PERMISSIONS } from '@core-interfaces/rbac/rbac-permission-codes';
import { sessionHasPermission } from '@core-interfaces/rbac/session-permissions';

import {
  ButtonCloseDirective,
  ContainerComponent,
  ShadowOnScrollDirective,
  SidebarBrandComponent,
  SidebarComponent,
  SidebarHeaderComponent,
  SidebarNavComponent,
  SidebarToggleDirective,
  SidebarTogglerDirective,
  ToasterComponent,
  type INavData,
} from '@coreui/angular-pro';

import { RealtimeToastComponent } from '../../components/realtime-toast/realtime-toast.component';
import { StickyBannerComponent } from '../../components/sticky-banner/sticky-banner.component';
import {
  DefaultFooterComponent,
  DefaultHeaderComponent,
} from '.';
import { navItems } from './_nav';
import { RegistrationReviewNotificationsService } from '@infra-adapters/services/registration-review-notifications.service';
import { AppToastService } from '@infra-adapters/services/app-toast.service';

function isOverflown(element: HTMLElement) {
  return (
    element.scrollHeight > element.clientHeight ||
    element.scrollWidth > element.clientWidth
  );
}

@Component({
  selector: 'app-dashboard',
  templateUrl: './default-layout.component.html',
  styleUrls: ['./default-layout.component.scss'],
  imports: [
    SidebarComponent,
    SidebarHeaderComponent,
    SidebarBrandComponent,

    NgScrollbar,
    SidebarNavComponent,

    SidebarToggleDirective,
    SidebarTogglerDirective,
    DefaultHeaderComponent,
    ShadowOnScrollDirective,
    ContainerComponent,
    RouterOutlet,
    DefaultFooterComponent,
    StickyBannerComponent,
    ButtonCloseDirective,
    ToasterComponent,
  ],
})
export class DefaultLayoutComponent implements OnInit {
  private readonly auth = inject(AuthManager);
  private readonly sessionExpiry = inject(SessionExpiryRedirectService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly registrationNotifications = inject(
    RegistrationReviewNotificationsService,
  );
  private readonly appToasts = inject(AppToastService);
  private readonly prestacionPerms = inject(MedicalRecordBackofficePermissionsPolicy);

  @ViewChild(ToasterComponent) private toaster?: ToasterComponent;

  private subscribeToastStream(
    stream$: import('rxjs').Observable<
      import('@infra-adapters/services/registration-review-notifications.service').RealtimeNotificationToastPayload
    >,
  ): void {
    stream$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((req) => {
        const toaster = this.toaster;
        if (!toaster) {
          return;
        }
        const props: Record<string, unknown> = {
          title: req.title,
          body: req.body,
          color: req.color ?? 'info',
          autohide: true,
          delay: 6000,
          fade: true,
          closeButton: true,
          subtitle: req.subtitle ?? '',
          categoryLabel: req.categoryLabel ?? '',
          iconName: req.iconName ?? '',
          lines: req.lines ?? [],
          timeLabel: req.timeLabel ?? '',
        };
        if (req.linkCommands?.length) {
          props['link'] = req.linkCommands;
        }
        toaster.addToast(
          RealtimeToastComponent,
          props as Parameters<ToasterComponent['addToast']>[1],
          {},
        );
      });
  }

  constructor() {
    afterNextRender(() => {
      this.subscribeToastStream(this.appToasts.toastRequest$);
      this.registrationNotifications.toastRequest$
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((req) => {
          const toaster = this.toaster;
          if (!toaster) {
            return;
          }
          const props: Record<string, unknown> = {
            title: req.title,
            body: req.body,
            color: req.color ?? 'info',
            autohide: true,
            delay: 10000,
            fade: true,
            closeButton: true,
            subtitle: req.subtitle ?? '',
            categoryLabel: req.categoryLabel ?? '',
            iconName: req.iconName ?? '',
            lines: req.lines ?? [],
            timeLabel: req.timeLabel ?? '',
          };
          if (req.linkCommands?.length) {
            props['link'] = req.linkCommands;
          }
          toaster.addToast(
            RealtimeToastComponent,
            props as Parameters<ToasterComponent['addToast']>[1],
            {},
          );
        });
    });
  }

  ngOnInit(): void {
    this.sessionExpiry.armWatch();
    void this.registrationNotifications.syncWithSession();
  }

  /** Entradas de menú según rol. Submenús con 1 hijo se promueven a ítem directo. */
  readonly navItemsFiltered = computed<INavData[]>(() => {
    const session = this.auth.UserSessionData();

    const urlAllowed: Record<string, boolean> = {
      '/dashboard': canViewDashboardFromSession(session),
      '/prestaciones': this.prestacionPerms.canAccessPrestacionesFromSession(session),
      '/usuarios-admin': canManageUsersFromSession(session),
      '/roles-admin': canManageRbacFromSession(session),
      '/archivos-almacenamiento': canManageAttachmentsAdminFromSession(session),
      '/ia-incidentes': canViewAiIncidentsFromSession(session),
      '/tickets-ayuda': canManageUsersFromSession(session),
      '/membresias-admin': canReadBillingFromSession(session),
      '/terminos-admin': canManageTermsFromSession(session),
      '/banners-admin': canManageBannersFromSession(session),
      '/bandeja-operacional': sessionHasPermission(
        session,
        RBAC_PERMISSIONS.BANDEJA_OPERACIONAL_READ,
      ),
      '/notificaciones': sessionHasPermission(
        session,
        RBAC_PERMISSIONS.NOTIFICATIONS_READ,
      ),
      '/notificaciones/configuracion': sessionHasPermission(
        session,
        RBAC_PERMISSIONS.NOTIFICATIONS_READ,
      ),
    };

    const isUrlAllowed = (url: unknown) =>
      typeof url !== 'string' || urlAllowed[url] !== false;

    const result: INavData[] = [];
    for (const item of navItems) {
      // Títulos de sección: solo mostrar si al menos un item siguiente es visible
      if (item.title) {
        result.push(item);
        continue;
      }
      // Item sin hijos: filtrar por permiso
      if (!item.children?.length) {
        if (isUrlAllowed(item.url)) {
          result.push(item);
        }
        continue;
      }
      // Submenú: filtrar hijos
      const allowedChildren = item.children.filter((c) => isUrlAllowed(c.url));
      if (allowedChildren.length === 0) continue;
      if (allowedChildren.length === 1) {
        // Promover hijo único a ítem directo
        result.push({
          ...allowedChildren[0],
          iconComponent: allowedChildren[0].iconComponent ?? item.iconComponent,
        });
      } else {
        result.push({ ...item, children: allowedChildren });
      }
    }

    // Limpiar títulos de sección huérfanos (sin ítems después)
    return result.filter((item, i) => {
      if (!item.title) return true;
      const next = result[i + 1];
      return next != null && !next.title;
    });
  });

  onScrollbarUpdate($event: any) {
    // if ($event.verticalUsed) {
    // console.log('verticalUsed', $event.verticalUsed);
    // }
  }
}
